// Generic on-tile filter chrome (TASK-495). One compact bar driven entirely by
// FILTER_WHITELIST[component] — no per-widget bespoke filter UI. It reflects the
// widget's active filter values and calls onChange with a single-key partial when
// the RM edits a control, so a summoned view can be refined without re-asking.
// Keeping it whitelist-driven means the bar stays in lockstep with the contract the
// UI brain (TASK-499) prop-whitelists against: one source of truth, no drift.
import type { FilterSpec } from '../../registry/filterContract';
import { FILTER_WHITELIST } from '../../registry/filterContract';
import { cn } from '../../lib/utils';

interface WidgetFilterBarProps {
  component: string;
  values: Record<string, unknown>;
  onChange: (partial: Record<string, unknown>) => void;
  // Keys to render disabled (e.g. whitelisted-but-not-yet-backend-honoured, Tier B).
  // The control still shows so the contract is visible, but the RM can't toggle a
  // filter that would silently do nothing.
  disabledKeys?: ReadonlySet<string>;
  className?: string;
}

const humanize = (v: string) => v.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const chipClass = (active: boolean, disabled?: boolean) =>
  cn(
    'rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors',
    disabled
      ? 'cursor-not-allowed bg-muted text-muted-foreground/40'
      : active
        ? 'bg-kielis-blue text-white'
        : 'bg-muted text-muted-foreground hover:bg-accent',
  );

const fieldClass = 'w-24 rounded-md border border-border bg-card px-2 py-0.5 text-[11px] disabled:opacity-40';

// Renders the control(s) for one whitelisted filter key. Emits the new value (or
// `undefined` to clear) for that key; the bar wraps it back into a {key: value} partial.
function FilterControl({
  spec,
  fieldKey,
  value,
  disabled,
  onValue,
}: {
  spec: FilterSpec;
  fieldKey: string;
  value: unknown;
  disabled?: boolean;
  onValue: (v: unknown) => void;
}) {
  if (spec.type === 'enum') {
    return (
      <>
        {spec.values.map((opt) => {
          const active = spec.multi
            ? Array.isArray(value) && (value as string[]).includes(opt)
            : value === opt;
          return (
            <button
              key={opt}
              type="button"
              disabled={disabled}
              aria-pressed={active}
              className={chipClass(active, disabled)}
              onClick={() => {
                if (spec.multi) {
                  const cur = Array.isArray(value) ? (value as string[]) : [];
                  const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
                  onValue(next.length ? next : undefined);
                } else {
                  onValue(value === opt ? undefined : opt);
                }
              }}
            >
              {humanize(opt)}
            </button>
          );
        })}
      </>
    );
  }

  if (spec.type === 'number') {
    return (
      <input
        type="number"
        disabled={disabled}
        value={typeof value === 'number' ? value : ''}
        min={spec.min}
        max={spec.max}
        aria-label={humanize(fieldKey)}
        className={fieldClass}
        onChange={(e) => onValue(e.target.value === '' ? undefined : Number(e.target.value))}
      />
    );
  }

  if (spec.type === 'boolean') {
    const active = value === true;
    return (
      <button
        type="button"
        disabled={disabled}
        aria-pressed={active}
        className={chipClass(active, disabled)}
        onClick={() => onValue(active ? undefined : true)}
      >
        {humanize(fieldKey)}
      </button>
    );
  }

  // string
  return (
    <input
      type="text"
      disabled={disabled}
      value={typeof value === 'string' ? value : ''}
      maxLength={spec.maxLength}
      placeholder={humanize(fieldKey)}
      aria-label={humanize(fieldKey)}
      className={cn(fieldClass, 'w-32')}
      onChange={(e) => onValue(e.target.value || undefined)}
    />
  );
}

export function WidgetFilterBar({ component, values, onChange, disabledKeys, className }: WidgetFilterBarProps) {
  const specs = FILTER_WHITELIST[component];
  if (!specs) return null;

  return (
    <div
      role="group"
      aria-label="Filters"
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border bg-muted/40 px-3 py-2',
        className,
      )}
    >
      {Object.entries(specs).map(([key, spec]) => {
        const disabled = disabledKeys?.has(key);
        return (
          <div
            key={key}
            className="flex items-center gap-1"
            title={disabled ? 'Pending backend support' : undefined}
          >
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
              {humanize(key)}
            </span>
            <FilterControl
              spec={spec}
              fieldKey={key}
              value={values[key]}
              disabled={disabled}
              onValue={(v) => onChange({ [key]: v })}
            />
          </div>
        );
      })}
    </div>
  );
}
